import axios from "axios";

const API_Path = process.env.REACT_APP_API_URL || "http://localhost:8000";

const defaultHeaders = {
  "Content-Type": "application/json",
};

const getStoredUser = () => {
  try {
    const raw = localStorage.getItem("userToken");
    return raw ? JSON.parse(raw) : null;
  } catch {
    localStorage.removeItem("userToken");
    return null;
  }
};

// Store logout callback for token expiration handling
let logoutCallback = null;

// Prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshPromise = null;

/**
 * Set the logout callback function to be called when token expiration is detected
 * @param {Function} callback - Function to call when 401/403 is detected
 */
export const setLogoutCallback = (callback) => {
  logoutCallback = callback;
};

/**
 * Attempt to refresh the access token using the refresh token.
 * Returns the new tokens on success, or null on failure.
 */
const refreshAccessToken = async () => {
  const storedUser = getStoredUser();
  if (!storedUser?.refreshToken) return null;

  // If already refreshing, wait for the existing attempt
  if (isRefreshing) return refreshPromise;

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const response = await axios({
        method: "post",
        url: API_Path + "/user/refresh-token",
        headers: {
          Authorization: `Bearer ${storedUser.refreshToken}`,
        },
      });

      if (response.status === 200 && response.data?.accessToken) {
        const updatedToken = {
          ...storedUser,
          accessToken: response.data.accessToken,
          refreshToken: response.data.refreshToken,
          role: response.data.role,
          planType: response.data.planType,
          planDaysLeft: response.data.planDaysLeft,
          account_state: response.data.account_state,
          next_action: response.data.next_action,
          entitled: response.data.entitled,
        };
        localStorage.setItem("userToken", JSON.stringify(updatedToken));
        return updatedToken;
      }
      return null;
    } catch {
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

const sendRequest = async (method, url, data, headers = {}) => {
  const apiUrl = API_Path + url;

  const stored = getStoredUser();
  const accessToken = stored?.accessToken || null;
  const finalHeaders = { ...headers };
  if (accessToken) {
    finalHeaders.Authorization = `Bearer ${accessToken}`;
  }

  // Let axios set Content-Type automatically for FormData (includes boundary)
  if (data instanceof FormData) {
    delete finalHeaders["Content-Type"];
  }

  try {
    const response = await axios({
      method,
      url: apiUrl,
      data,
      headers: finalHeaders,
    });
    return { status: response?.status, data: response?.data };
  } catch (error) {
    const status = error?.response?.status;
    const errorData = error?.response?.data;

    // On 401, try a token refresh once before giving up. Only 401 — a 403
    // means the server knows who you are but you can't do this, which a
    // refreshed token won't fix. Skip the flow entirely if the user wasn't
    // logged in to begin with.
    if (status === 401 && accessToken) {
      const newTokens = await refreshAccessToken();
      const retryToken = newTokens?.accessToken || null;

      if (retryToken) {
        try {
          const retryHeaders = {
            ...headers,
            Authorization: `Bearer ${retryToken}`,
          };
          if (data instanceof FormData) {
            delete retryHeaders["Content-Type"];
          }
          const retryResponse = await axios({
            method,
            url: apiUrl,
            data,
            headers: retryHeaders,
          });
          return { status: retryResponse?.status, data: retryResponse?.data };
        } catch (retryError) {
          if (retryError?.response?.status === 401 && logoutCallback) {
            logoutCallback();
          }
          return {
            status: retryError?.response?.status,
            data: retryError?.response?.data,
            error: retryError.message,
          };
        }
      }

      // Refresh failed — log out
      if (logoutCallback) logoutCallback();
    }

    return { status, data: errorData, error: error.message };
  }
};

export const doPost = async (url, body, headers) =>
  sendRequest("post", url, body, { ...defaultHeaders, ...headers });
export const doGet = async (url, headers) =>
  sendRequest("get", url, null, { ...defaultHeaders, ...headers });
export const doPatch = async (url, body, headers) =>
  sendRequest("patch", url, body, { ...defaultHeaders, ...headers });
export const doPut = async (url, body, headers) =>
  sendRequest("put", url, body, { ...defaultHeaders, ...headers });
export const doDelete = async (url, body, headers) =>
  sendRequest("delete", url, body, { ...defaultHeaders, ...headers });
